/**
 * IssueAffectedBookings
 * Shows block/unblock status and upcoming bookings affected by an issue's inventory item.
 * For each affected booking, shows inline alternative items (same product type) that can
 * replace the broken item. Follows the BlockingPeriodConflictsModal pattern:
 * - Shows current item info
 * - Per-booking inline alternatives with size display & same-size highlighting
 * - Uses change_booking_allocation RPC for reassignment
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Unlock, Loader2, Calendar, Check, Search, ShieldAlert, Pencil, X as XIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getAffectedBookings, blockItemForIssue, unblockItemForIssue } from '../services/issues.service';
import { useAuth } from '../contexts/AuthContext';
import { insertAuditLog } from '../services/bookingAuditLog.service';
import { updateBlockingTime } from '../services/inventoryBlockingTimes.service';
import { useToast } from '../contexts/ToastContext';
import { formatAllocationFailure, type AllocationRpcResult } from '../lib/allocationRpc';
import { useDateFormatter } from '../hooks/useDateFormatter';

interface AffectedBooking {
  id: string; // booking_item_id
  inventory_item_id: string;
  item_name: string;
  bookings: {
    id: string;
    order_id: string;
    customer_firstname: string;
    customer_lastname: string;
    start_date: string;
    end_date: string;
    booking_state: string;
  };
}

interface AlternativeItem {
  id: string;
  nr: number | null;
  item_number: string | null;
  combination_code: string | null;
  size: string | null;
  active: boolean;
  isIdentical: boolean;
}

interface IssueAffectedBookingsProps {
  issueId: string;
  inventoryItemId: string;
  productTypeId: string | null;
  productName?: string;
  itemNumber: string | null;
  itemSize: string | null;
  merchantId: string;
  blockingTimeId: string | null;
  repairDueDate: string | null;
  userId: string;
  onBlockingChanged?: (blockingTimeId: string | null) => void;
}

export default function IssueAffectedBookings({
  issueId,
  inventoryItemId,
  productTypeId,
  productName,
  itemNumber,
  itemSize,
  merchantId,
  blockingTimeId,
  repairDueDate,
  userId,
  onBlockingChanged,
}: IssueAffectedBookingsProps) {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const { showToast } = useToast();
  const { formatDateShort } = useDateFormatter();
  const [bookings, setBookings] = useState<AffectedBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [blocking, setBlocking] = useState(false);
  const isBlocked = !!blockingTimeId;

  // Block date management
  const [blockedUntil, setBlockedUntil] = useState<string | null>(null);
  const [showBlockDatePicker, setShowBlockDatePicker] = useState(false);
  const [blockDateValue, setBlockDateValue] = useState('');
  const [editingBlockDate, setEditingBlockDate] = useState(false);
  const [savingBlockDate, setSavingBlockDate] = useState(false);

  // Alternatives per booking (keyed by booking_item_id)
  const [alternativesMap, setAlternativesMap] = useState<Map<string, AlternativeItem[]>>(new Map());
  const [loadingAlts, setLoadingAlts] = useState(false);
  // Selections: booking_item_id → selected alternative inventory item id
  const [selections, setSelections] = useState<Map<string, string>>(new Map());
  // Skipped bookings
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  // Search per booking
  const [searchQueries, setSearchQueries] = useState<Map<string, string>>(new Map());
  // Saving state
  const [saving, setSaving] = useState(false);

  // The bookings shown as "affected" are those overlapping the item's
  // unavailability window. When the item is blocked that window ends at the
  // blocking period's blocked_until (which the user can extend), otherwise at
  // the repair due date. Use whichever ends later so extending the block past
  // the due date, or a due date past the block, both widen the list.
  const affectedUntil = (() => {
    const candidates: number[] = [];
    if (blockedUntil) candidates.push(new Date(blockedUntil).getTime());
    if (repairDueDate) candidates.push(new Date(repairDueDate + 'T23:59:59.999').getTime());
    if (candidates.length === 0) return null;
    return new Date(Math.max(...candidates)).toISOString();
  })();

  useEffect(() => {
    loadAffectedBookings();
  }, [inventoryItemId, affectedUntil]);

  // Fetch blocked_until when blockingTimeId changes
  useEffect(() => {
    if (blockingTimeId) {
      supabase
        .from('inventory_blocking_times')
        .select('blocked_until')
        .eq('id', blockingTimeId)
        .single()
        .then(({ data }) => {
          if (data?.blocked_until) {
            setBlockedUntil(data.blocked_until);
          }
        });
    } else {
      setBlockedUntil(null);
    }
  }, [blockingTimeId]);

  async function loadAffectedBookings() {
    setLoading(true);
    const { data } = await getAffectedBookings(inventoryItemId, affectedUntil);
    const affectedBookings = (data || []) as AffectedBooking[];
    setBookings(affectedBookings);
    setSelections(new Map());
    setSkipped(new Set());
    setSearchQueries(new Map());

    if (affectedBookings.length > 0 && productTypeId) {
      await loadAlternatives(affectedBookings);
    }
    setLoading(false);
  }

  async function loadAlternatives(affectedBookings: AffectedBooking[]) {
    if (!productTypeId) return;
    setLoadingAlts(true);

    try {
      // Get all active items of same product type, excluding the broken item
      const { data: sameTypeItems, error: itemsError } = await supabase
        .from('inventory_items')
        .select('id, nr, item_number, combination_code, size, active')
        .eq('product_type_id', productTypeId)
        .neq('id', inventoryItemId)
        .eq('active', true)
        .order('nr', { ascending: true });

      if (itemsError) throw itemsError;

      const candidateItems = (sameTypeItems || []) as Array<{
        id: string; nr: number | null; item_number: string | null;
        combination_code: string | null; size: string | null; active: boolean;
      }>;

      const candidateIds = candidateItems.map(i => i.id);

      // Get booking conflicts for candidates
      let bookingConflicts: any[] = [];
      if (candidateIds.length > 0) {
        const { data: conflicts } = await supabase
          .from('booking_items')
          .select(`
            id,
            inventory_item_id,
            bookings!inner(
              id,
              start_date,
              end_date,
              booking_state
            )
          `)
          .in('inventory_item_id', candidateIds)
          .in('bookings.booking_state', ['open', 'active']);

        bookingConflicts = conflicts || [];
      }

      // Get blocking periods for candidates
      let candidateBlockingTimes: any[] = [];
      if (candidateIds.length > 0) {
        const { data: blockings } = await supabase
          .from('inventory_blocking_times')
          .select('*')
          .in('inventory_item_id', candidateIds);

        candidateBlockingTimes = blockings || [];
      }

      // Build alternatives per affected booking
      const newAlternativesMap = new Map<string, AlternativeItem[]>();

      for (const booking of affectedBookings) {
        const bStart = new Date(booking.bookings.start_date);
        const bEnd = new Date(booking.bookings.end_date);

        const alternatives: AlternativeItem[] = [];

        for (const candidate of candidateItems) {
          // Check booking conflict
          const hasBookingConflict = bookingConflicts.some((c: any) => {
            if (c.inventory_item_id !== candidate.id) return false;
            const cb = c.bookings;
            if (!cb) return false;
            return new Date(cb.start_date) < bEnd && new Date(cb.end_date) > bStart;
          });
          if (hasBookingConflict) continue;

          // Check blocking period conflict
          const hasBlockingConflict = candidateBlockingTimes.some((bt: any) => {
            if (bt.inventory_item_id !== candidate.id) return false;
            return new Date(bt.blocked_from) < bEnd && new Date(bt.blocked_until) > bStart;
          });
          if (hasBlockingConflict) continue;

          alternatives.push({
            ...candidate,
            isIdentical: candidate.size === itemSize,
          });
        }

        // Sort: identical size first, then by nr
        alternatives.sort((a, b) => {
          if (a.isIdentical && !b.isIdentical) return -1;
          if (!a.isIdentical && b.isIdentical) return 1;
          return (a.nr || 0) - (b.nr || 0);
        });

        newAlternativesMap.set(booking.id, alternatives);
      }

      setAlternativesMap(newAlternativesMap);
    } catch (err) {
      console.error('Error loading alternatives:', err);
      showToast(t('issueAffectedBookings.failedToLoadAlternatives'), 'error');
    } finally {
      setLoadingAlts(false);
    }
  }

  function handleSelect(bookingItemId: string, itemId: string) {
    setSelections(prev => {
      const next = new Map(prev);
      next.set(bookingItemId, itemId);
      return next;
    });
    setSkipped(prev => {
      const next = new Set(prev);
      next.delete(bookingItemId);
      return next;
    });
  }

  function handleSkipToggle(bookingItemId: string) {
    setSkipped(prev => {
      const next = new Set(prev);
      if (next.has(bookingItemId)) {
        next.delete(bookingItemId);
      } else {
        next.add(bookingItemId);
        setSelections(p => { const n = new Map(p); n.delete(bookingItemId); return n; });
      }
      return next;
    });
  }

  // Filter out alternatives already selected for other bookings
  function getAvailableAlternatives(bookingItemId: string): AlternativeItem[] {
    const allAlts = alternativesMap.get(bookingItemId) || [];
    const takenIds = new Set<string>();
    for (const [biId, selectedItemId] of selections) {
      if (biId !== bookingItemId) takenIds.add(selectedItemId);
    }
    return allAlts.filter(alt => !takenIds.has(alt.id));
  }

  async function handleReassign(bookingItemId: string) {
    const selectedId = selections.get(bookingItemId);
    if (!selectedId) return;

    setSaving(true);
    try {
      const { data, error } = await (supabase.rpc as any)('change_booking_allocation', {
        p_booking_item_id: bookingItemId,
        p_new_inventory_item_id: selectedId,
        p_swap_with_booking_item_id: null,
        p_current_inventory_item_id: inventoryItemId,
        p_skip_buffer_check: true,
      });

      if (error) throw error;
      const result = data as AllocationRpcResult | null;
      if (!result?.ok) {
        throw new Error(result ? formatAllocationFailure(result, t) : t('changeAllocation.failedToChange'));
      }

      // Log the reallocation to the booking's audit log (fire-and-forget)
      const affectedBooking = bookings.find(b => b.id === bookingItemId);
      const selectedAlt = alternativesMap.get(bookingItemId)?.find(a => a.id === selectedId);
      if (affectedBooking) {
        insertAuditLog({
          bookingId: affectedBooking.bookings.id,
          merchantId,
          changeType: 'inventory_reallocated',
          changeDetails: {
            product_name: affectedBooking.item_name || productName || 'Unknown',
            from_item: itemNumber,
            to_item: selectedAlt?.item_number || (selectedAlt?.nr ? `#${selectedAlt.nr}` : null),
            reason: 'issue',
            issue_id: issueId,
          },
          userId: user?.id,
          userEmail: profile?.full_name || user?.email || null,
          triggeredBy: 'admin_panel',
        });
      }

      showToast(t('issueAffectedBookings.itemReplaced'), 'success');
      // Reload to reflect changes
      await loadAffectedBookings();
    } catch (err) {
      console.error('Error reassigning:', err);
      showToast(t('issueAffectedBookings.failedToReplace'), 'error');
    } finally {
      setSaving(false);
    }
  }

  function handleBlock() {
    // Pre-fill with repair due date if available, otherwise empty
    setBlockDateValue(repairDueDate || '');
    setShowBlockDatePicker(true);
  }

  async function handleConfirmBlock() {
    if (blockDateValue) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const chosen = new Date(blockDateValue + 'T00:00:00');
      if (chosen <= today) {
        showToast(t('issueAffectedBookings.blockDateFuture'), 'error');
        return;
      }
    }

    setBlocking(true);
    const until = blockDateValue
      ? new Date(blockDateValue + 'T23:59:59').toISOString()
      : undefined;
    const { data, error } = await blockItemForIssue(issueId, inventoryItemId, userId, until);
    if (error) {
      showToast(t('issueAffectedBookings.failedToBlock') + ': ' + error, 'error');
    } else {
      showToast(t('issueAffectedBookings.itemBlocked'), 'success');
      onBlockingChanged?.(data?.blocking_time_id || null);
    }
    setBlocking(false);
    setShowBlockDatePicker(false);
  }

  async function handleSaveBlockDate() {
    if (!blockingTimeId || !blockDateValue) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const chosen = new Date(blockDateValue + 'T00:00:00');
    if (chosen <= today) {
      showToast(t('issueAffectedBookings.blockDateFuture'), 'error');
      return;
    }

    setSavingBlockDate(true);
    const { error } = await updateBlockingTime(blockingTimeId, {
      blocked_until: new Date(blockDateValue + 'T23:59:59').toISOString(),
    });
    if (error) {
      showToast(t('issueAffectedBookings.failedToUpdateBlockDate') + ': ' + error, 'error');
    } else {
      setBlockedUntil(new Date(blockDateValue + 'T23:59:59').toISOString());
      showToast(t('issueAffectedBookings.blockDateUpdated'), 'success');
    }
    setSavingBlockDate(false);
    setEditingBlockDate(false);
  }

  async function handleUnblock() {
    if (!blockingTimeId) return;
    setBlocking(true);
    const { error } = await unblockItemForIssue(issueId, blockingTimeId, userId);
    if (error) {
      showToast(t('issueAffectedBookings.failedToUnblock') + ': ' + error, 'error');
    } else {
      showToast(t('issueAffectedBookings.itemUnblocked'), 'success');
      onBlockingChanged?.(null);
    }
    setBlocking(false);
  }

  // Format blocked_until as YYYY-MM-DD for the date input
  const blockedUntilDate = blockedUntil
    ? new Date(blockedUntil).toISOString().split('T')[0]
    : null;
  const isBlockExpired = blockedUntil && new Date(blockedUntil) < new Date();

  return (
    <div className="space-y-md">
      {/* Block status */}
      <div className="p-md rounded-lg border border-border space-y-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-sm">
            {isBlocked ? (
              <>
                <div className="w-2 h-2 rounded-full bg-danger" />
                <span className="text-sm font-medium text-danger">{t('issueAffectedBookings.itemIsBlocked')}</span>
              </>
            ) : (
              <>
                <div className="w-2 h-2 rounded-full bg-success" />
                <span className="text-sm font-medium text-success">{t('issueAffectedBookings.itemNotBlocked')}</span>
              </>
            )}
          </div>
          <button
            onClick={isBlocked ? handleUnblock : handleBlock}
            disabled={blocking}
            className={`btn btn-sm ${isBlocked ? 'btn-ghost' : 'btn-ghost text-danger hover:bg-danger/10'}`}
          >
            {blocking ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isBlocked ? (
              <>
                <Unlock className="w-4 h-4" />
                {t('issueAffectedBookings.unblock')}
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" />
                {t('issueAffectedBookings.blockItem')}
              </>
            )}
          </button>
        </div>

        {/* Block end date — shown when blocked */}
        {isBlocked && blockedUntilDate && (
          <div className="flex items-center justify-between pt-sm border-t border-border">
            <div className="flex items-center gap-sm">
              <Calendar className="w-4 h-4 text-secondary" />
              <span className="text-sm text-secondary">{t('issueAffectedBookings.blockedUntil')}:</span>
              {editingBlockDate ? (
                <div className="flex items-center gap-xs">
                  <input
                    type="date"
                    value={blockDateValue}
                    onChange={(e) => setBlockDateValue(e.target.value)}
                    className="input text-sm py-xs px-sm"
                  />
                  <button onClick={handleSaveBlockDate} className="btn-icon text-success" disabled={savingBlockDate}>
                    {savingBlockDate ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  </button>
                  <button onClick={() => setEditingBlockDate(false)} className="btn-icon text-secondary">
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { setBlockDateValue(blockedUntilDate); setEditingBlockDate(true); }}
                  className="flex items-center gap-sm px-sm py-xs rounded-lg border border-border hover:border-blue transition-colors"
                >
                  <span className={`text-sm font-medium ${isBlockExpired ? 'text-danger' : 'text-primary'}`}>
                    {formatDateShort(blockedUntilDate)}
                  </span>
                  <Pencil className="w-3 h-3 text-secondary" />
                </button>
              )}
              {isBlockExpired && !editingBlockDate && (
                <span className="inline-flex items-center px-sm py-0.5 rounded-full text-xs font-semibold bg-danger/10 text-danger">
                  {t('issueAffectedBookings.expired')}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Date picker for new block — shown when user clicks Block Item */}
        {showBlockDatePicker && !isBlocked && (
          <div className="pt-sm border-t border-border space-y-sm">
            <div className="flex items-center gap-sm">
              <Calendar className="w-4 h-4 text-secondary" />
              <span className="text-sm text-secondary">{t('issueAffectedBookings.blockUntil')}:</span>
              <input
                type="date"
                value={blockDateValue}
                onChange={(e) => setBlockDateValue(e.target.value)}
                className="input text-sm py-xs px-sm"
              />
            </div>
            {!blockDateValue && (
              <p className="text-xs text-secondary">{t('issueAffectedBookings.leaveEmptyDefault')}</p>
            )}
            <div className="flex items-center gap-sm">
              <button
                onClick={handleConfirmBlock}
                disabled={blocking}
                className="btn btn-sm btn-primary"
              >
                {blocking ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    {t('issueAffectedBookings.confirmBlock')}
                  </>
                )}
              </button>
              <button
                onClick={() => setShowBlockDatePicker(false)}
                className="btn btn-sm btn-ghost"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bookings list with inline alternatives */}
      {loading ? (
        <div className="flex items-center gap-sm text-secondary text-sm py-md">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('issueAffectedBookings.loadingBookings')}
        </div>
      ) : bookings.length === 0 ? (
        <p className="text-sm text-secondary py-sm">{t('issueAffectedBookings.noUpcomingBookings')}</p>
      ) : (
        <div className="space-y-md">
          <h4 className="text-sm font-semibold text-primary">{t('issueAffectedBookings.affectedBookings')}</h4>
          <p className="text-xs text-secondary">
            {t('issueAffectedBookings.bookingsUseItem', { count: bookings.length })}
            {repairDueDate ? ` (${t('issueAffectedBookings.until')} ${formatDateShort(repairDueDate)})` : ''}.
            {t('issueAffectedBookings.replaceInstructions')}
          </p>

          {bookings.map((b) => {
            const availableAlts = getAvailableAlternatives(b.id);
            const selectedId = selections.get(b.id);
            const isSkipped = skipped.has(b.id);
            const searchQuery = searchQueries.get(b.id) || '';
            const filtered = searchQuery.trim()
              ? availableAlts.filter(item =>
                  item.item_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  item.combination_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  item.nr?.toString().includes(searchQuery) ||
                  item.size?.toLowerCase().includes(searchQuery.toLowerCase())
                )
              : availableAlts;

            return (
              <div key={b.id} className="border border-border rounded-lg overflow-hidden">
                {/* Booking header */}
                <div className="p-md bg-warning/5 border-b border-border">
                  <div className="flex items-center gap-sm">
                    <Calendar className="w-4 h-4 text-warning flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-primary">
                        #{b.bookings.order_id}
                      </span>
                      <span className="text-sm text-secondary ml-sm">
                        — {b.bookings.customer_firstname} {b.bookings.customer_lastname}
                      </span>
                    </div>
                    {selectedId && <Check className="w-4 h-4 text-success flex-shrink-0" />}
                    {isSkipped && <ShieldAlert className="w-4 h-4 text-warning flex-shrink-0" />}
                  </div>
                  <p className="text-xs text-secondary mt-xs">
                    {formatDateShort(b.bookings.start_date)} – {formatDateShort(b.bookings.end_date)}
                    {productName && <span className="ml-md">· {productName}</span>}
                  </p>
                </div>

                {/* Alternatives section */}
                <div className="p-md">
                  {loadingAlts ? (
                    <div className="flex items-center gap-sm text-sm text-secondary py-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('issueAffectedBookings.loadingAlternatives')}
                    </div>
                  ) : isSkipped ? (
                    <div className="p-sm bg-warning/5 border border-warning/30 rounded-lg">
                      <p className="text-sm text-warning">
                        {t('issueAffectedBookings.willKeepCurrentItem')}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleSkipToggle(b.id)}
                        className="text-xs text-blue underline mt-xs"
                      >
                        {t('issueAffectedBookings.chooseReplacement')}
                      </button>
                    </div>
                  ) : availableAlts.length === 0 ? (
                    <div>
                      <p className="text-sm text-secondary py-sm">
                        {t('issueAffectedBookings.noAlternatives')}
                      </p>
                      <label className="flex items-start gap-sm cursor-pointer mt-sm p-sm bg-warning/5 border border-warning/30 rounded-lg">
                        <input
                          type="checkbox"
                          checked={isSkipped}
                          onChange={() => handleSkipToggle(b.id)}
                          className="mt-0.5"
                        />
                        <span className="text-sm text-secondary">
                          {t('issueAffectedBookings.skipKeepCurrent')}
                        </span>
                      </label>
                    </div>
                  ) : (
                    <>
                      <p className="text-xs text-secondary mb-sm">
                        {t('issueAffectedBookings.selectReplacement')} <span className="font-medium">{itemNumber || t('issueAffectedBookings.theBrokenItem')}</span> {t('issueAffectedBookings.fromThisBooking')}:
                      </p>

                      {/* Search (only when many items) */}
                      {availableAlts.length > 5 && (
                        <div className="relative mb-sm">
                          <Search className="absolute left-sm top-1/2 -translate-y-1/2 w-4 h-4 text-secondary" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={e => setSearchQueries(prev => new Map(prev).set(b.id, e.target.value))}
                            placeholder={t('resolveConflicts.searchItems')}
                            className="input text-sm pl-10 w-full"
                          />
                        </div>
                      )}

                      <div className="space-y-xs" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {filtered.map(item => {
                          const isSelected = selectedId === item.id;

                          return (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => handleSelect(b.id, item.id)}
                              className={`w-full flex items-center gap-md p-sm rounded-lg border text-left transition-colors ${
                                isSelected
                                  ? 'border-blue bg-blue/10'
                                  : 'border-border hover:border-blue hover:bg-blue/5'
                              }`}
                            >
                              {/* Selection indicator */}
                              <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                                isSelected ? 'border-blue bg-blue' : 'border-secondary'
                              }`}>
                                {isSelected && <Check className="w-3 h-3 text-white" />}
                              </div>

                              {/* Item info */}
                              <div className="flex-1 min-w-0 flex items-center gap-md flex-wrap">
                                <span className="font-medium text-primary text-sm">
                                  {item.item_number || `#${item.nr}`}
                                </span>
                                {item.combination_code && (
                                  <span className="text-xs text-secondary">
                                    {item.combination_code}
                                  </span>
                                )}
                                {item.size && (
                                  <span className="text-xs text-secondary">
                                    {t('changeAllocation.sizeLabel')}: {item.size}
                                  </span>
                                )}
                              </div>

                              {/* Badges */}
                              <div className="flex items-center gap-xs flex-shrink-0">
                                {item.isIdentical && (
                                  <span className="badge badge-success text-xs">{t('changeAllocation.sameSize')}</span>
                                )}
                                {!item.isIdentical && item.size && itemSize && (
                                  <span className="badge badge-warning text-xs">{t('changeAllocation.differentSize')}</span>
                                )}
                                <span className="badge badge-success text-xs">{t('changeAllocation.available')}</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      {/* Skip option */}
                      <div className="mt-sm">
                        <label className="flex items-start gap-sm cursor-pointer p-sm bg-warning/5 border border-warning/30 rounded-lg">
                          <input
                            type="checkbox"
                            checked={isSkipped}
                            onChange={() => handleSkipToggle(b.id)}
                            className="mt-0.5"
                          />
                          <span className="text-sm text-secondary">
                            {t('issueAffectedBookings.skipKeepCurrent')}
                          </span>
                        </label>
                      </div>

                      {/* Reassign button (per booking) */}
                      {selectedId && (
                        <div className="mt-sm flex justify-end">
                          <button
                            onClick={() => handleReassign(b.id)}
                            disabled={saving}
                            className="btn btn-sm btn-primary"
                          >
                            {saving ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                {t('issueAffectedBookings.replacing')}
                              </>
                            ) : (
                              <>
                                <Check className="w-4 h-4" />
                                {t('issueAffectedBookings.replaceItem')}
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
